import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { AthletePackage, PaginatedAthletePackageListList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface QueryKeyType {
  pageParam?: UrlParams;
}

interface IUseAthletePackages {
  athlete?: string;
  status?: AthletePackage['status'];
  statuses?: AthletePackage['status'][];
  limit?: string;
  enabled?: boolean;
}

function useAthletePackages({
  athlete,
  status,
  statuses,
  limit = '20',
  enabled = true,
}: IUseAthletePackages): UseInfiniteQueryResult<AxiosResponse<PaginatedAthletePackageListList>> {
  const fetchAthletePackages = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedAthletePackageListList>> => {
      const params = pageParam;

      if (athlete !== undefined) {
        params.athlete = athlete;
      }
      if (status !== undefined) {
        params.status = status;
      }
      if (statuses && statuses.length > 0) {
        params.statuses = statuses.join(',');
      }
      return client.get(`athlete-packages/?${encodeUrlParams(params)}`);
    },
    [athlete, status, statuses, limit],
  );

  // limit belongs in the key: it changes what a page holds, so two callers asking for the same
  // athlete and statuses at different page sizes must not share one cache entry. Without it a
  // caller fetching a single row to count it would serve that one row to a full table.
  return useInfiniteQuery(['athletePackages', athlete, status, statuses, limit], fetchAthletePackages, {
    getNextPageParam: page => {
      return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
    },
    enabled: enabled && !!athlete,
  });
}

export default useAthletePackages;
